import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { AgpFormUtil } from '@liberty-financial/agp-shared';
import * as _ from 'lodash';

import {
  OpportunityRequirement,
  OpportunitySchema
} from '@features/opportunities/modules/core/models/schema-property-definition';

export namespace OpportunitySchemaUtil {
  function updateValidatorsForNumberProperty(
    control: AbstractControl,
    definition: OpportunitySchema.NumberPropertyDefinition
  ) {
    const { min, max } = definition.range;
    const validators = [];
    if (_.isFinite(min)) {
      validators.push(Validators.min(min as number));
    }
    if (_.isFinite(max)) {
      validators.push(Validators.max(max as number));
    }
    if (validators.length) {
      AgpFormUtil.Validators.updateOnCondition(control, true, validators);
    }
  }

  export function attachFormGroupToSchema(formGroup: FormGroup, schema: any) {
    _.forEach(formGroup.controls, (control, key) => {
      const definition = schema[key];
      if (!definition) {
        return;
      }

      if (control instanceof FormGroup) {
        attachFormGroupToSchema(control, definition);
        return;
      }
      attachFormControlToSchema(control, definition);
    });
  }

  export function attachFormControlToSchema(
    control: AbstractControl,
    definition: any,
    additionalCondition: boolean = true
  ) {
    if (OpportunitySchema.isPropertyDefinition(definition)) {
      AgpFormUtil.Validators.updateRequiredIfNecessary(
        control,
        OpportunityRequirement.isFundamentallyRequired(
          definition.requirement
        ) && additionalCondition
      );

      if (OpportunitySchema.isNumberPropertyDefinition(definition)) {
        updateValidatorsForNumberProperty(control, definition);
      }
    }
  }

  export function detachFormGroupToSchema(formGroup: FormGroup, schema: any) {
    let newSchema: any = {};
    _.forEach(formGroup.controls, (control, key) => {
      const definition = schema[key];
      if (!definition) {
        return;
      }

      if (control instanceof FormGroup) {
        newSchema[key] = detachFormGroupToSchema(control, definition);
        return;
      }
      newSchema[key] = detachFormControlToSchema(control, definition);
    });
    return newSchema;
  }

  export function detachFormControlToSchema(
    control: AbstractControl,
    definition: any
  ) {
    attachFormControlToSchema(control, definition, false);
    return resetDefinition(definition);
  }

  function resetDefinition(definition: any) {
    if (OpportunitySchema.isPropertyDefinition(definition)) {
      return { ...definition, requirement: null };
    }
    return definition;
  }

  export function idFromReference(value: string): string {
    let index = value.indexOf('/');
    return index < 0 || index + 1 >= value.length
      ? value
      : value.substring(index + 1);
  }

  export namespace Transform {
    export namespace Date {
      export function toModel(
        value?: string | null,
        translation?: (val: string) => string
      ): string | null {
        if (!value) {
          return null;
        }

        if (translation) {
          value = translation(value);
        }
        return value;
      }

      export function toSchemaContract(
        value?: string | null,
        translation?: (val: string) => string | null
      ): string | null | undefined {
        if (_.isString(value) && value === '') {
          return null;
        }

        if (value === null || value === undefined) {
          return value;
        }

        if (translation) {
          const translated = translation(value);
          if (translated === null || translated === undefined) {
            return null;
          }
          value = translated;
        }
        return value;
      }
    }
    export namespace Boolean {
      export function toModel(value?: boolean | null): boolean | null {
        return _.isBoolean(value) ? value : null;
      }
    }
    export namespace Number {
      export function toModel(value?: number | null): number | null {
        return value ?? null;
      }
    }
  }
}
